'use client';
import React, { useState } from 'react';
import StatusBadge from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';
import type { ItemMaster, ItemCategory } from './ItemMasterContent';

const CATEGORY_LABELS: Record<ItemCategory, string> = {
  RAW_MATERIAL: 'Raw Material',
  CONSUMABLE: 'Consumable',
  SPARE_PART: 'Spare Part',
  TOOL: 'Tool',
  SAFETY_ITEM: 'Safety Item',
  PPE: 'PPE',
  FINISHED_GOOD: 'Finished Good',
  OTHER: 'Other',
};

const CATEGORY_COLORS: Record<ItemCategory, string> = {
  RAW_MATERIAL: 'bg-blue-50 text-blue-700 border-blue-200',
  CONSUMABLE: 'bg-orange-50 text-orange-700 border-orange-200',
  SPARE_PART: 'bg-purple-50 text-purple-700 border-purple-200',
  TOOL: 'bg-gray-100 text-gray-700 border-gray-200',
  SAFETY_ITEM: 'bg-red-50 text-red-700 border-red-200',
  PPE: 'bg-amber-50 text-amber-700 border-amber-200',
  FINISHED_GOOD: 'bg-teal-50 text-teal-700 border-teal-200',
  OTHER: 'bg-slate-100 text-slate-600 border-slate-200',
};

type SortKey = 'itemCode' | 'itemName' | 'category' | 'unitCost' | 'reorderLevel';

interface TableProps {
  items: ItemMaster[];
  selectedIds: string[];
  onSelectionChange: (ids: string[]) => void;
  visibleCols: { hsnCode: boolean; barcode: boolean; lastMovement: boolean };
}

export default function ItemMasterTable({ items, selectedIds, onSelectionChange, visibleCols }: TableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('itemCode');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [deactivateTarget, setDeactivateTarget] = useState<string | null>(null);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);

  const sorted = [...items].sort((a, b) => {
    const av = a[sortKey];
    const bv = b[sortKey];
    if (typeof av === 'string' && typeof bv === 'string') {
      return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    }
    if (typeof av === 'number' && typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    return 0;
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const toggleSelect = (id: string) => {
    onSelectionChange(
      selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === paginated.length) onSelectionChange([]);
    else onSelectionChange(paginated.map((i) => i.id));
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <Icon
      name={sortKey === col ? (sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon') : 'ChevronUpDownIcon'}
      size={12}
      className={sortKey === col ? 'text-primary' : 'text-muted-foreground/50'}
    />
  );

  const headers: { label: string; key?: SortKey; always?: boolean }[] = [
    { label: 'Item Code', key: 'itemCode', always: true },
    { label: 'Item Name', key: 'itemName', always: true },
    { label: 'Category', key: 'category', always: true },
    { label: 'UOM', always: true },
    { label: 'Reorder Lvl', key: 'reorderLevel', always: true },
    { label: 'Safety Stock', always: true },
    { label: 'Unit Cost (€)', key: 'unitCost', always: true },
    { label: 'QHSE', always: true },
    { label: 'Expiry', always: true },
    ...(visibleCols.hsnCode ? [{ label: 'HSN Code', always: false }] : []),
    ...(visibleCols.barcode ? [{ label: 'Barcode', always: false }] : []),
    ...(visibleCols.lastMovement ? [{ label: 'Last Movement', always: false }] : []),
    { label: 'Status', always: true },
    { label: 'Actions', always: true },
  ];

  if (items.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-16 text-center shadow-card">
        <div className="w-12 h-12 bg-muted rounded-xl flex items-center justify-center mx-auto mb-4">
          <Icon name="CubeIcon" size={24} className="text-muted-foreground" />
        </div>
        <h3 className="text-sm font-600 text-foreground">No items match your filters</h3>
        <p className="text-xs text-muted-foreground mt-1.5">Try adjusting your search or category filter to find items.</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-card border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.length === paginated.length && paginated.length > 0}
                    onChange={toggleAll}
                    className="w-4 h-4 rounded border-border accent-primary"
                  />
                </th>
                {headers.map((h) => (
                  <th
                    key={`th-${h.label}`}
                    onClick={h.key ? () => handleSort(h.key!) : undefined}
                    className={`text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-3 py-3 whitespace-nowrap ${h.key ? 'cursor-pointer hover:text-foreground select-none' : ''}`}
                  >
                    <span className="flex items-center gap-1">
                      {h.label}
                      {h.key && <SortIcon col={h.key} />}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paginated.map((item, idx) => (
                <tr
                  key={item.id}
                  onMouseEnter={() => setHoveredRow(item.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  className={`transition-colors ${idx % 2 === 1 ? 'bg-muted/20' : ''} ${hoveredRow === item.id ? 'bg-secondary/5' : ''} ${!item.isActive ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(item.id)}
                      onChange={() => toggleSelect(item.id)}
                      className="w-4 h-4 rounded border-border accent-primary"
                    />
                  </td>
                  <td className="px-3 py-3">
                    <span className="font-mono-data text-xs text-foreground font-500">{item.itemCode}</span>
                  </td>
                  <td className="px-3 py-3 max-w-[180px]">
                    <span className="text-sm text-foreground font-500 truncate block">{item.itemName}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex items-center border rounded-full text-[10px] font-500 px-2 py-0.5 whitespace-nowrap ${CATEGORY_COLORS[item.category]}`}>
                      {CATEGORY_LABELS[item.category]}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground font-500">{item.uom}</td>
                  <td className="px-3 py-3 text-xs tabular-nums text-foreground">{item.reorderLevel.toLocaleString('de-DE')}</td>
                  <td className="px-3 py-3 text-xs tabular-nums text-foreground">{item.safetyStock.toLocaleString('de-DE')}</td>
                  <td className="px-3 py-3 text-xs tabular-nums font-600 text-foreground">
                    € {item.unitCost.toFixed(2)}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {item.isQhseItem ? (
                      <span title="QHSE Item" className="inline-flex items-center justify-center w-5 h-5 bg-teal/10 rounded-full">
                        <Icon name="ShieldCheckIcon" size={12} className="text-teal" />
                      </span>
                    ) : (
                      <span className="text-muted-foreground/30 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center">
                    {item.hasExpiry ? (
                      <span title="Has Expiry" className="inline-flex items-center justify-center w-5 h-5 bg-amber-50 rounded-full">
                        <Icon name="ClockIcon" size={12} className="text-amber-600" />
                      </span>
                    ) : (
                      <span className="text-muted-foreground/30 text-xs">—</span>
                    )}
                  </td>
                  {visibleCols.hsnCode && (
                    <td className="px-3 py-3">
                      <span className="font-mono-data text-xs text-muted-foreground">{item.hsnCode}</span>
                    </td>
                  )}
                  {visibleCols.barcode && (
                    <td className="px-3 py-3">
                      <span className="font-mono-data text-xs text-muted-foreground">{item.barcode}</span>
                    </td>
                  )}
                  {visibleCols.lastMovement && (
                    <td className="px-3 py-3 text-xs text-muted-foreground whitespace-nowrap">{item.lastMovement}</td>
                  )}
                  <td className="px-3 py-3">
                    <StatusBadge status={item.isActive ? 'ACTIVE' : 'DRAFT'} size="sm" />
                  </td>
                  <td className="px-3 py-3">
                    <div className={`flex items-center gap-1 transition-opacity ${hoveredRow === item.id ? 'opacity-100' : 'opacity-0'}`}>
                      <button
                        title="Edit item"
                        onClick={() => toast.info(`Edit item ${item.itemCode}`)}
                        className="p-1.5 rounded-md hover:bg-secondary/10 text-secondary transition-colors"
                      >
                        <Icon name="PencilSquareIcon" size={14} />
                      </button>
                      <button
                        title="Duplicate item"
                        onClick={() => toast.success(`Duplicated ${item.itemCode}`)}
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name="DocumentDuplicateIcon" size={14} />
                      </button>
                      <button
                        title="Print barcode label"
                        onClick={() => toast.info(`Printing barcode for ${item.itemCode}`)}
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name="PrinterIcon" size={14} />
                      </button>
                      <button
                        title={item.isActive ? 'Deactivate item' : 'Activate item'}
                        onClick={() => setDeactivateTarget(item.id)}
                        className={`p-1.5 rounded-md transition-colors ${item.isActive ? 'hover:bg-red-50 text-red-500 hover:text-red-600' : 'hover:bg-teal/10 text-teal'}`}
                      >
                        <Icon name={item.isActive ? 'NoSymbolIcon' : 'CheckCircleIcon'} size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <span className="text-xs text-muted-foreground">
            Showing {Math.min((page - 1) * perPage + 1, sorted.length)}–{Math.min(page * perPage, sorted.length)} of {sorted.length} items
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="p-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronLeftIcon" size={14} />
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={`page-${i + 1}`}
                onClick={() => setPage(i + 1)}
                className={`w-8 h-8 rounded-md text-xs font-500 border transition-colors ${
                  page === i + 1 ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted text-foreground'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="p-1.5 rounded-md border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Icon name="ChevronRightIcon" size={14} />
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={() => {
          toast.success('Item deactivated successfully.');
          setDeactivateTarget(null);
        }}
        title="Deactivate Item"
        message="This item will be marked inactive and hidden from new GRN/GI entries. Existing stock records and transactions will be preserved."
        confirmLabel="Deactivate Item"
        variant="warning"
      />
    </>
  );
}